/**
 * Portal routes (PRD §12 "Initial Portal Screens", user-facing set).
 *
 * Three groups:
 *  - public credential screens (sign in, sign up, verification, reset)
 *  - holding pages that a valid but not-yet-usable account lands on
 *  - the account area behind `ProtectedRoute`
 */
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from '@/components/app-layout';
import { ProtectedRoute } from '@/components/protected-route';
import { AuthProvider } from '@/hooks/use-auth';
import { AcceptInvitationPage } from '@/pages/accept-invitation';
import { AdminOrganizationsPage } from '@/pages/admin-organizations';
import { AccessDeniedPage } from '@/pages/access-denied';
import { AwaitingApprovalPage } from '@/pages/awaiting-approval';
import { ChangePasswordPage } from '@/pages/change-password';
import { ChooseOrganizationPage } from '@/pages/choose-organization';
import { ForgotPasswordPage } from '@/pages/forgot-password';
import { NotFoundPage } from '@/pages/not-found';
import { ProfilePage } from '@/pages/profile';
import { ResetPasswordPage } from '@/pages/reset-password';
import { SelectOrganizationPage } from '@/pages/select-organization';
import { OrganizationPeoplePage } from '@/pages/organization-people';
import { SessionsPage } from '@/pages/sessions';
import { SignInPage } from '@/pages/sign-in';
import { SignOutPage } from '@/pages/sign-out';
import { SignUpPage } from '@/pages/sign-up';
import { VerifyEmailPage } from '@/pages/verify-email';

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<Navigate to="/sign-in" replace />} />

          {/* Public credential screens */}
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/sign-up" element={<SignUpPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/*
            Invitation acceptance. Public because the invitee usually has no
            account yet — the page handles the signed-out, signed-in-matching
            and signed-in-as-someone-else cases itself, which a
            `ProtectedRoute` redirect would flatten into a lost invitation.
          */}
          <Route path="/accept-invitation" element={<AcceptInvitationPage />} />

          {/*
            Steps inside a module's authorization flow. Not behind
            `ProtectedRoute`: each handles its own signed-out case by returning
            the user to sign-in with the flow handle intact, rather than losing
            the module's request to a generic redirect.
          */}
          <Route path="/select-organization" element={<ChooseOrganizationPage />} />

          {/* Reachable with or without a usable session */}
          <Route path="/access-denied" element={<AccessDeniedPage />} />
          <Route path="/awaiting-approval" element={<AwaitingApprovalPage />} />
          <Route path="/sign-out" element={<SignOutPage />} />

          {/*
            Staff area. Behind the same session guard as the account area; the
            routes themselves additionally require a platform role server-side,
            so a signed-in customer reaching this URL sees a refusal rather
            than data.
          */}
          <Route element={<ProtectedRoute />}>
            <Route path="/admin" element={<AppLayout />}>
              <Route index element={<AdminOrganizationsPage />} />
              <Route path="organizations" element={<AdminOrganizationsPage />} />
            </Route>
          </Route>

          {/* Account area */}
          <Route element={<ProtectedRoute />}>
            <Route path="/account" element={<AppLayout />}>
              <Route index element={<ProfilePage />} />
              <Route path="organizations" element={<SelectOrganizationPage />} />
              <Route path="people" element={<OrganizationPeoplePage />} />
              <Route path="sessions" element={<SessionsPage />} />
              <Route path="password" element={<ChangePasswordPage />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
